import type { MessageDescriptor } from 'react-intl';
import type { CollectionTicketClaimMemberStateDTO } from '@/api/trafikportalen';
import type { CollectionTicketClaimMemberStateDTOState } from '@/api/trafikportalen/models/collectionTicketClaimMemberStateDTOState';
import type { LocaleMessages } from '@/locales/locale-config';

export interface ClaimStateConfig {
  label: MessageDescriptor;
  description: MessageDescriptor;
  color: string;
}

const claimMessage = (id: keyof LocaleMessages): MessageDescriptor => ({
  id,
});

export const CONSECUTIVE_FAILURES_LABEL = claimMessage(
  'claim.consecutiveFailures',
);
export const CONSECUTIVE_FAILURES_DESCRIPTION = claimMessage(
  'claim.consecutiveFailures.description',
);

export const SCHEDULED_LABEL = claimMessage('claim.state.scheduled.label');
export const SCHEDULED_DESCRIPTION = claimMessage(
  'claim.state.scheduled.description',
);

export const CLAIM_ERROR_STATES: CollectionTicketClaimMemberStateDTOState[] = [
  'card-order-failed',
  'purchase-failed',
  'unresolvable-relational-ticket',
  'unfulfillable',
];

export const isClaimErrorState = (
  state: CollectionTicketClaimMemberStateDTOState,
) => CLAIM_ERROR_STATES.includes(state);

const PRODUCTS_TENANT_PATTERN = /\bproducts\.(\w+)/gi;

export const findOtherTenantInClaimMessage = (
  memberState: CollectionTicketClaimMemberStateDTO | undefined,
  tenantId: string,
): string | undefined => {
  if (memberState?.state !== 'unresolvable-relational-ticket') return undefined;
  const tenants = Array.from(
    memberState.message?.matchAll(PRODUCTS_TENANT_PATTERN) ?? [],
    ([, tenant]) => tenant,
  );
  return tenants
    .find((tenant) => tenant.toLowerCase() !== tenantId.toLowerCase())
    ?.toUpperCase();
};

const claimState = (
  state: CollectionTicketClaimMemberStateDTOState,
  color: string,
): ClaimStateConfig => ({
  label: claimMessage(`claim.state.${state}.label`),
  description: claimMessage(`claim.state.${state}.description`),
  color,
});

const inProgressColor = 'bg-blue-100 text-blue-800';
const errorColor = 'bg-red-100 text-red-800';
const successColor = 'bg-green-100 text-green-800';

export const CLAIM_STATES: Record<
  CollectionTicketClaimMemberStateDTOState,
  ClaimStateConfig
> = {
  'card-ordered': claimState('card-ordered', inProgressColor),
  'awaiting-card-transfer': claimState(
    'awaiting-card-transfer',
    inProgressColor,
  ),
  'card-order-failed': claimState('card-order-failed', errorColor),
  'purchase-ordered': claimState('purchase-ordered', inProgressColor),
  'purchase-failed': claimState('purchase-failed', errorColor),
  'unresolvable-relational-ticket': claimState(
    'unresolvable-relational-ticket',
    errorColor,
  ),
  fulfilled: claimState('fulfilled', successColor),
  unfulfillable: claimState('unfulfillable', errorColor),
};
